import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Button
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DataTable from '../common/DataTable';
import CompactFilterPanel from '../common/CompactFilterPanel';
import { ApiService } from '../../services/api';
import { getErrorSeverity } from '../../utils/errorUtils';
import { useEditMode } from '../../hooks/useEditMode';
import { EditModeConfig } from '../../types/editMode';
import {
  EditModeControls,
  DeleteConfirmationDialog,
  RefreshNotificationDialog,
  EditWarningDialog,
} from '../common/editMode';
import { useEditWarning } from '../../hooks/useEditWarning';
import { usePermissions } from '../../hooks/usePermissions';

interface VerificationStatus {
  id: number;
  name: string;
  status: number;
  observations: string | null;
}

interface VerificationStatusTableColumn {
  id: keyof VerificationStatus;
  label: string;
  minWidth: number;
  type: 'number' | 'text' | 'boolean';
}

interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

const VerificationStatusView: React.FC = () => {
  const { canEdit } = usePermissions();
  
  const [verificationStatuses, setVerificationStatuses] = useState<VerificationStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false,
    nextCursor: null as number | null,
    limit: 50
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: 'id', direction: 'ASC' });
  const scrollPositionRef = useRef<number>(0);

  // Define all available columns
  const allColumns: VerificationStatusTableColumn[] = [
    { id: 'id', label: 'ID', minWidth: 80, type: 'number' },
    { id: 'name', label: 'Name', minWidth: 200, type: 'text' },
    { id: 'status', label: 'Status', minWidth: 100, type: 'number' },
    { id: 'observations', label: 'Observations', minWidth: 300, type: 'text' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => ({ id: col.id as string, label: col.label, visible: true }))
  );

  const columns = allColumns.filter(col => 
    visibleColumns.find(vc => vc.id === col.id)?.visible
  );

  // Load verification statuses data
  const loadVerificationStatuses = useCallback(async (cursor: number | null = null, append: boolean = false, refresh?: boolean) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await ApiService.getVerificationStatusRecords(
        cursor,
        pagination.limit,
        sortConfig.column,
        sortConfig.direction,
        refresh
      );
      
      if (append) {
        setVerificationStatuses(prev => [...prev, ...response.data]);
      } else {
        setVerificationStatuses(response.data);
        scrollPositionRef.current = 0;
      }
      
      setPagination({
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
        nextCursor: response.pagination.nextCursor,
        limit: response.pagination.limit
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load verification statuses');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [pagination.limit, sortConfig]);

  // Initial load
  useEffect(() => {
    loadVerificationStatuses(null, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    if (pagination.hasMore && !isLoadingMore && !loading) {
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      loadVerificationStatuses(pagination.nextCursor, true);
    }
  };

  const handleSort = useCallback((column: string) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'ASC' ? 'DESC' : 'ASC'
    }));
  }, []);

  const handleColumnToggle = (columnId: string, visible: boolean) => {
    setVisibleColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible } : col
      )
    );
  };

  // Use the edit warning hook
  const editWarning = useEditWarning('editWarningShown_global', () => {
    editMode.handleEdit();
  });

  const handleEditClick = () => {
    if (editMode.isEditMode) {
      editMode.handleCancel();
      return;
    }
    editWarning.handleEditClick();
  };

  // Edit Mode Configuration
  const editModeConfig: EditModeConfig<VerificationStatus> = {
    apiMethods: {
      create: (data: any) => ApiService.createVerificationStatusRecord(data),
      update: (id: number, data: any) => ApiService.updateVerificationStatusRecord(id, data),
      delete: (id: number) => ApiService.deleteVerificationStatusRecord(id),
    },
    fields: {
      id: { type: 'number', required: false, editable: false },
      name: { type: 'text', required: true, editable: true },
      status: { type: 'number', required: true, editable: true, defaultValue: 1 },
      observations: { type: 'text', required: false, editable: true, defaultValue: null },
    },
    primaryKey: 'id',
    displayName: 'Verification Status',
    validate: (row, isNew) => {
      if (isNew) {
        const nameValue = row.name;
        const trimmedName = nameValue ? String(nameValue).trim() : '';
        if (!trimmedName) {
          return 'Name is required for new rows';
        }
      }
      return null;
    },
    prepareData: (row, originalRow) => {
      // For new rows, ensure all fields are properly set
      if (!originalRow) {
        return {
          name: row.name ? String(row.name).trim() : '',
          status: row.status !== undefined ? row.status : 1,
          observations: row.observations !== undefined ? row.observations : null,
        };
      }
      
      // OPTIMIZATION: For updates, ONLY process fields that are in the row (changed fields)
      const updateData: Partial<VerificationStatus> = {};
      
      // Only process fields that actually changed (exist in row)
      Object.keys(row).forEach(key => {
        const fieldKey = key as keyof VerificationStatus;
        
        if (fieldKey === 'name') {
          updateData.name = row.name ? String(row.name).trim() : '';
        } else if (fieldKey === 'status') {
          updateData.status = row.status !== undefined ? row.status : 1;
        } else if (fieldKey === 'observations') {
          updateData.observations = row.observations !== undefined ? row.observations : null;
        }
      });
      
      return updateData;
    },
  };

  // Use the edit mode hook
  const editMode = useEditMode(editModeConfig, {
    data: verificationStatuses,
    onDataChange: setVerificationStatuses,
    onRefresh: async () => {
      await loadVerificationStatuses(null, false);
    },
    onError: (errorMessage) => {
      setError(errorMessage);
    },
  });


  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 0.5
      }}>
        
        {error && (
          <Alert severity={getErrorSeverity(error)} sx={{ m: 2, mt: 0 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CompactFilterPanel
              columns={visibleColumns}
              onColumnToggle={handleColumnToggle}
              onFiltersApply={() => {}}
              onClearFilters={() => {}}
              onEdit={canEdit ? handleEditClick : undefined}
              onAddNew={() => {}}
              filterType="state"
              showFilters={false}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              onClick={() => {
                // Exit edit mode if active
                if (editMode.isEditMode) {
                  editMode.handleCancel();
                }
                // Reload data with cache refresh
                loadVerificationStatuses(null, false, true);
              }}
              sx={{ minWidth: 'auto', padding: '8px' }}
            >
              <RefreshIcon />
            </Button>
          </Box>
        </Box>
        
        {/* Edit Mode Controls */}
        {canEdit && (
          <EditModeControls
            isEditMode={editMode.isEditMode}
            saving={editMode.saving}
            onSave={editMode.handleSave}
            onCancel={editMode.handleCancel}
            onAddNew={editMode.handleAddNew}
            selectedRowsRef={editMode.selectedRowsRef}
            onDeleteClick={editMode.handleDeleteClick}
            deleteDialogRef={editMode.deleteDialogRef}
            refreshNotificationRef={editMode.refreshNotificationRef}
          />
        )}
      </Box>
      
      {/* Table Section */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden',
        }}>
          <DataTable
            columns={columns.map(col => ({
              id: col.id as string,
              label: col.label,
              minWidth: col.minWidth,
              type: col.type,
            }))}
            rows={editMode.displayRows}
            page={0}
            rowsPerPage={editMode.displayRows.length}
            totalRows={pagination.total}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            onSort={handleSort}
            sortConfig={sortConfig}
            loading={loading}
            isEditMode={editMode.isEditMode}
            onCellEdit={editMode.handleCellEdit}
            editableColumns={columns.filter(col => col.id !== 'id').map(col => col.id as string)}
            isRowSelected={editMode.isRowSelected}
            selectedRowsCount={editMode.selectedRowsCount}
            selectAllVersion={editMode.selectAllVersion}
            onRowSelect={editMode.handleRowSelect}
            onSelectAll={editMode.handleSelectAll}
            hidePagination={true}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2, mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {verificationStatuses.length} of {pagination.total}
          </Typography>
          {isLoadingMore ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
          ) : pagination.hasMore ? (
          <button 
            onClick={handleLoadMore}
            disabled={isLoadingMore || loading}
            style={{
              padding: '6px 16px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#1976d2',
              backgroundColor: 'transparent',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              opacity: (isLoadingMore || loading) ? 0.6 : 1
            }}
            onMouseOver={(e) => {
              if (isLoadingMore || loading) return;
              e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Load More
          </button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No more records
            </Typography>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        dialogRef={editMode.deleteDialogRef}
        selectedRowsRef={editMode.selectedRowsRef}
        onConfirm={editMode.handleConfirmDelete}
      />

      {/* Refresh Notification Dialog */}
      <RefreshNotificationDialog
        dialogRef={editMode.refreshNotificationRef}
      />

      {/* Edit Warning Dialog */}
      <EditWarningDialog
        open={editWarning.showWarning}
        onClose={editWarning.handleCancel}
        onProceed={editWarning.handleProceed}
      />
    </Box>
  );
};

export default VerificationStatusView;

